import { SkyBadge, SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import type { SecretPointer } from "@shared/types/instances";
import type { BindingSecretCopy } from "@shared/types/instancesAdmin";
import { isSecretStored } from "@/lib/secretStored";

type Staged = string | null | BindingSecretCopy | undefined;

/** What the field shows: a held clear, value or copy first, else what the vault holds. */
function secretFieldState(
  staged: Staged,
  isStored: boolean,
): "cleared" | "staged" | "copy" | "stored" | "none" {
  if (staged === null) return "cleared";
  if (typeof staged === "string") return "staged";
  if (staged) return "copy";
  return isStored ? "stored" : "none";
}

/** Undo, Clear and Replace (or Set), each only when the caller offers it and it applies. */
function SecretFieldActions({
  testId,
  staged,
  isStored,
  error,
  onReplace,
  onClear,
  onDiscardStaged,
}: {
  testId: string;
  staged: Staged;
  isStored: boolean;
  error: string | undefined;
  onReplace: (() => void) | undefined;
  onClear: (() => void) | undefined;
  onDiscardStaged: (() => void) | undefined;
}) {
  return (
    <>
      {staged !== undefined && onDiscardStaged ? (
        <SkyButton
          appearance="subtle"
          size="small"
          testId={`${testId}-undo`}
          onClick={onDiscardStaged}
        >
          Undo
        </SkyButton>
      ) : null}
      {onClear && isStored && staged === undefined ? (
        <SkyButton appearance="subtle" size="small" testId={`${testId}-clear`} onClick={onClear}>
          Clear
        </SkyButton>
      ) : null}
      {onReplace ? (
        <SkyButton
          appearance="secondary"
          size="small"
          testId={`${testId}-replace`}
          // The server's sentence for this field, read with the control that fixes it.
          aria-describedby={error ? `${testId}-error` : undefined}
          onClick={onReplace}
        >
          {isStored ? "Replace" : "Set"}
        </SkyButton>
      ) : null}
    </>
  );
}

/**
 * A credential, rendered as the pointer to it and never as a value (SEC-2).
 *
 * The server answers `secret_ref` columns as `{ vaultName, secretName }` only, so
 * there is no value to render even if this component wanted one — which is the
 * point: the screen cannot leak what it was never given. The affordance is
 * **Replace**, not Edit, because the old value cannot be read, only overwritten.
 *
 * A staged replacement shows as "New value ready to save" rather than as the text
 * the user typed, so a shoulder-surfer gains nothing from an open form either. A
 * staged copy (B28, platform admins only) names its SOURCE secret, which is an
 * identifier and never the value.
 *
 * The vault and secret names are themselves internals (SEC-17): the server sends them only to a
 * caller who may change the secret, and everyone else gets `stored` instead, so the field says
 * "Stored" or "Not set" with no names.
 */
export function SecretReferenceField({
  label,
  pointer,
  stored,
  hint,
  staged,
  readOnly,
  readOnlyNote,
  error,
  onReplace,
  onClear,
  onDiscardStaged,
  testId,
}: {
  label: string;
  pointer: SecretPointer;
  /**
   * Whether a secret is stored when the server withheld its pointer (SEC-17: a caller without
   * `instance.settings.write` reads no vault or secret name). Shows "Stored" with no names.
   */
  stored?: boolean;
  hint?: string;
  /**
   * `undefined` = untouched, a string = a new value held for the save, `null` = clear, and
   * `{ copyFrom: { name } }` = a copy of an existing vault secret held for the save (B28).
   */
  staged?: string | null | BindingSecretCopy;
  readOnly?: boolean;
  readOnlyNote?: string;
  /** The server's sentence for this field from the last refused save, shown under it. */
  error?: string;
  onReplace?: () => void;
  onClear?: () => void;
  onDiscardStaged?: () => void;
  testId: string;
}) {
  const isStored = isSecretStored(pointer, stored);
  const state = secretFieldState(staged, isStored);

  return (
    <div className="space-y-1" data-testid={testId}>
      <SkyText as="label" size={200} weight="medium" block>
        {label}
      </SkyText>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-card p-2">
        {state === "stored" ? (
          <>
            <SkyBadge appearance="tint" color="subtle">
              Stored
            </SkyBadge>
            {pointer ? (
              <SkyText size={200} font="numeric" color="muted" data-testid={`${testId}-pointer`}>
                {pointer.vaultName} / {pointer.secretName}
              </SkyText>
            ) : null}
          </>
        ) : null}
        {state === "none" ? (
          <SkyText size={200} color="muted">
            Not set
          </SkyText>
        ) : null}
        {state === "staged" ? (
          <SkyBadge appearance="tint" color="warning">
            New value ready to save
          </SkyBadge>
        ) : null}
        {state === "cleared" ? (
          <SkyBadge appearance="tint" color="warning">
            Will be cleared on save
          </SkyBadge>
        ) : null}
        {state === "copy" && staged && typeof staged === "object" ? (
          <>
            <SkyBadge appearance="tint" color="warning">
              Copy ready to save
            </SkyBadge>
            <SkyText size={200} font="numeric" color="muted" data-testid={`${testId}-copy-source`}>
              from {staged.copyFrom.name}
            </SkyText>
          </>
        ) : null}

        <span className="ml-auto flex items-center gap-2">
          {readOnly ? (
            <SkyText size={200} color="muted">
              {readOnlyNote ?? "Platform admin only"}
            </SkyText>
          ) : (
            <SecretFieldActions
              testId={testId}
              staged={staged}
              isStored={isStored}
              error={error}
              onReplace={onReplace}
              onClear={onClear}
              onDiscardStaged={onDiscardStaged}
            />
          )}
        </span>
      </div>
      {error ? (
        // Not announced here: the save's own error bar (or notice) announces the refusal once,
        // and this line places the server's sentence at the field it names (ux-s10 §3).
        <SkyText
          as="p"
          size={200}
          color="danger"
          id={`${testId}-error`}
          data-testid={`${testId}-error`}
        >
          {error}
        </SkyText>
      ) : null}
      {hint ? (
        <SkyText as="p" size={200} color="muted">
          {hint}
        </SkyText>
      ) : null}
    </div>
  );
}
